import { useEffect, useCallback, useRef, RefObject } from 'react';
import { devLog } from '../../utils/devLogger';

// 🚨 DEBUG: Toggle validation on/off at runtime (ref-based, no re-renders)
// This is a module-level variable that can be toggled via the exported function
let VALIDATION_DISABLED = false;

// Export function to toggle validation (for debug button)
export const toggleValidation = (): boolean => {
  VALIDATION_DISABLED = !VALIDATION_DISABLED;
  devLog(`🔧 Validation ${VALIDATION_DISABLED ? 'DISABLED' : 'ENABLED'}`);
  
  // Dispatch event so UI can update (if needed)
  document.dispatchEvent(new CustomEvent('validationToggled', { 
    detail: { disabled: VALIDATION_DISABLED } 
  }));
  
  // If validation is being turned ON, trigger a validation check
  if (!VALIDATION_DISABLED) {
    // Small delay to ensure the toggle is complete before validation runs
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('validationNeeded'));
      devLog('🔧 Validation triggered after toggle ON');
    }, 100);
  }
  
  return VALIDATION_DISABLED;
};

// Export function to check current status
export const isValidationDisabled = (): boolean => VALIDATION_DISABLED;

interface ValidationError {
  id: string;
  source: string;
  target: string;
  sourceValue: number;
  targetValue: number;
  difference: number;
  contextRef: string;
  message: string;
  type?: 'balance' | 'mandatory' | 'conditional' | 'format'; // Add type to distinguish error types
  fieldName?: string; // For mandatory field errors
}

interface ValidationPair {
  source: { name: string };
  target: { name: string };
}

interface ConditionalFieldRule {
  targetField: string;
  value: string;
  requiredFields: string[];
  type?: 'static' | 'dynamic';
  getRequiredFields?: (value: string) => string[]; // For dynamic rules
}

interface UseValidationReturn {
  errors: ValidationError[];
  hasErrors: boolean;
  checkValidation: () => void;
  clearErrors: () => void;
  updateFieldValue: (fieldName: string, contextRef: string, value: number) => void;
  getSectionErrors: (sectionCode: string) => ValidationError[];
  getSectionErrorCount: (sectionCode: string) => number;
  hasFieldError: (fieldName: string, contextRef?: string) => boolean;
  getFieldError: (fieldName: string, contextRef?: string) => ValidationError | null;
}

interface UseValidationOptions {
  enabled?: boolean;
}

// Export ValidationError type so other components can use it
export type { ValidationError };

// ============================================================================
// STATIC CONSTANTS - Created ONCE at module load (not on every render)
// ============================================================================

// Validation pairs for balance checks
const VALIDATION_PAIRS: ValidationPair[] = [
  // SG direct pair: SOFP Assets vs injected computed total
  { source: { name: 'Assets' }, target: { name: 'Computed-EquityAndLiabilitiesTotal' } },

  // SG direct pair: SOPL ProfitLoss vs injected attributable total
  { source: { name: 'ProfitLoss' }, target: { name: 'Computed-ProfitLossAttributableTotal' } },

  // SG analysis direct pairs (same-page checks)
  { source: { name: 'TradeAndOtherReceivables' }, target: { name: 'Computed-TradeAndOtherReceivablesTotal' } },
  { source: { name: 'TradeAndOtherPayables' }, target: { name: 'Computed-TradeAndOtherPayablesTotal' } },
  { source: { name: 'LoansAndBorrowings' }, target: { name: 'Computed-LoansAndBorrowingsTotal' } },

  // SOCIE validations - SOFP Equity must equal SOCIE Closing Equity (MPERS/MFRS)
  { source: { name: 'Equity' }, target: { name: 'SOCIE-Equity' } },
  // SOCIE validations - SOFP Fund and Reserve must equal SOCIE Closing Fund Balance (CLBG)
  { source: { name: 'FundAndReserve' }, target: { name: 'SOCIE-FundBalance' } },
  // SOCIE validations - ProfitLoss (calculated total) must equal SOCIE ProfitLoss (in EquityMember column)
  { source: { name: 'ProfitLoss' }, target: { name: 'SOCIE-ProfitLoss' } }
  // NOTE: ComprehensiveIncome vs SOCIE-ComprehensiveIncome validation disabled for now to reduce indicator noise
];

// SG mandatory fields list (phase 1 migration from SG backup)
const MANDATORY_FIELDS: string[] = [
  'NameOfCompany',
  'UniqueEntityNumber',
  'TypeOfXBRLFiling',
  'NatureOfFinancialStatementsCompanyLevelOrConsolidated',
  'TypeOfAccountingStandardUsedToPrepareFinancialStatements',
  'TypeOfStatementOfFinancialPosition',
  'WhetherFinancialStatementsArePreparedOnGoingConcernBasis',
  'DescriptionOfPresentationCurrency',
  'LevelOfRoundingUsedInFinancialStatements',
  'CurrentPeriodStartDate',
  'CurrentPeriodEndDate',
  // 'PriorPeriodStartDate',
  'DateOfAuthorisationForIssueOfFinancialStatements',
  'TaxonomyVersion',
  'NameAndVersionOfSoftwareUsedToGenerateInstanceDocument',
  'HowWasXBRLInstanceDocumentPrepared',
  'DisclosureOfCompleteSetOfFinancialStatementsTextBlock',
  'CashFlowsFromUsedInOperatingActivities',
  'CashFlowsFromUsedInInvestingActivities',
  'CashFlowsFromUsedInFinancingActivities',
  'TypeOfAuditOpinionInIndependentAuditorsReport',
    //Additional Information
  'NumberOfEmployeesOfCompany',
  'NumberOfEmployeesOfGroup',
  'WhetherCompanyIsSubsidiary',
  'WhetherCompanyHasAnySubsidiaryAssociateOrJointVenture'
];

// SG section-level mandatory field mapping for section badge/error filtering
const SG_SECTION_MANDATORY_FIELDS: Record<string, string[]> = {
  '10000000_DisclosureOfFilingInformationAbstract': [
    'NameOfCompany',
    'UniqueEntityNumber',
    'TypeOfXBRLFiling',
    'NatureOfFinancialStatementsCompanyLevelOrConsolidated',
    'TypeOfAccountingStandardUsedToPrepareFinancialStatements',
    'TypeOfStatementOfFinancialPosition',
    'WhetherFinancialStatementsArePreparedOnGoingConcernBasis',
    'DescriptionOfPresentationCurrency',
    'LevelOfRoundingUsedInFinancialStatements',
    'CurrentPeriodStartDate',
    'CurrentPeriodEndDate',
    'PriorPeriodStartDate',
    'DateOfAuthorisationForIssueOfFinancialStatements',
    'TaxonomyVersion',
    'NameAndVersionOfSoftwareUsedToGenerateInstanceDocument',
    'HowWasXBRLInstanceDocumentPrepared',
  ],
  '11000000_DisclosureOfCompleteSetOfFinancialStatementsAbstract': [
    'DisclosureOfCompleteSetOfFinancialStatementsTextBlock',
  ],
  '23000000_StatementOfCashFlowsAbstract': [
    'CashFlowsFromUsedInOperatingActivities',
    'CashFlowsFromUsedInInvestingActivities',
    'CashFlowsFromUsedInFinancingActivities',
  ],
  '22000000_StatementOfProfitOrLossAbstract': [
    'Revenue',
    'ProfitLossBeforeTaxation',
    'TaxExpenseBenefitContinuingOperations',
    'ProfitLoss',
    'AmortisationExpense',
  ],
  '13000000_DisclosuresInIndependentAuditorsReportAbstract': [
    'TypeOfAuditOpinionInIndependentAuditorsReport',
    'ReasonForModifiedAuditOpinionRelatedToImpairmentOfAssets',
    'ReasonForModifiedAuditOpinionRelatedToGoingConcern',
    'ReasonForModifiedAuditOpinionRelatedToAuditOfOpeningBalance',
    'ReasonForModifiedAuditOpinionRelatedToConsolidatedOrEquityAccounting',
    'ReasonForModifiedAuditOpinionRelatedToOngoingInvestigationOrLegalCases',
    'ReasonForModifiedAuditOpinionRelatedToOthers',
  ],
  '14000000_DisclosureOfAdditionalInformationAbstract': [
    'NumberOfEmployeesOfCompany',
    'NumberOfEmployeesOfGroup',
    'WhetherCompanyIsSubsidiary',
    'WhetherCompanyHasAnySubsidiaryAssociateOrJointVenture',
  ],
  '38000000_DisclosureOfSelectedIncomeExpensesAbstract': ['DepreciationExpense'],
  '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract': ['DepreciationPropertyPlantAndEquipment'],
  '33000000_DisclosureOfIntangibleAssetsAbstract': ['AmortisationExpenseIntangibleAssets'],
  '39000000_RelatedPartyTransactionsAbstract': ['RevenueFromRelatedPartyTransactions'],
};

// SG phase-2 conditional rules (non-calculation)
const SG_AUDIT_MODIFIED_REASON_FIELDS: string[] = [
  'ReasonForModifiedAuditOpinionRelatedToImpairmentOfAssets',
  'ReasonForModifiedAuditOpinionRelatedToGoingConcern',
  'ReasonForModifiedAuditOpinionRelatedToAuditOfOpeningBalance',
  'ReasonForModifiedAuditOpinionRelatedToConsolidatedOrEquityAccounting',
  'ReasonForModifiedAuditOpinionRelatedToOngoingInvestigationOrLegalCases',
  'ReasonForModifiedAuditOpinionRelatedToOthers',
];


  
// ============================================================================
// END STATIC CONSTANTS
// ============================================================================

/**
 * DOM-BASED Validation (matches 1.0 validation.js)
 * - Stores errors in ref (NO React state, NO re-renders)
 * - Directly manipulates DOM to show/hide error indicators
 * - Much faster and doesn't interfere with user typing
 */
export const useValidationSG = (
  formDataRef?: RefObject<Record<string, string>>,
  options?: UseValidationOptions
): UseValidationReturn => {
  const enabled = options?.enabled ?? true;

  // CRITICAL: Store errors in REF, not state (no re-renders!)
  const errorsRef = useRef<ValidationError[]>([]);
  // Cache for standardContext -> contextRef mapping (one-time DOM scan)
  const contextMappingCacheRef = useRef<{ mapping: Map<string, string>, timestamp: number } | null>(null);
  // Track if initial load is complete - validation should only run after this
  const initialLoadCompleteRef = useRef<boolean>(false);
  // 🔒 Prevent concurrent DOM updates (critical for UPSERT safety)
  const isUpdatingDOMRef = useRef<boolean>(false);
  // 🔑 PERF FIX: Track last values to skip validation if value didn't change
  const lastValueMapRef = useRef<Map<string, string>>(new Map());

  // Humanize a field name like "NumberOfDirectorsSigningDirectorsReport" -> "Number Of Directors Signing Directors Report"
  const humanizeFieldName = useCallback((fieldName: string): string => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  // Validate Audit Firm Registration Number format (AF + 4 digits if ≤1801 OR AF + 6 digits if ≥1802)
  const validateAuditFirmNumber = useCallback((value: string): boolean => {
    if (!value.toUpperCase().startsWith('AF')) return false;
    const numberPart = value.substring(2);
    if (!/^\d+$/.test(numberPart)) return false;
    if (numberPart.length === 4) {
      return parseInt(numberPart) <= 1801;
    } else if (numberPart.length === 6) {
      return parseInt(numberPart) >= 1802;
    }
    return false;
  }, []);
  
  // Helper: Check if field exists in form (skip validation if field doesn't exist)
  const fieldExistsInForm = useCallback((fieldName: string, contextRef?: string): boolean => {
    const isQname = fieldName.includes(':');
    const attrName = isQname ? 'data-qname' : 'name';

    if (contextRef) {
      const el = document.querySelector(
        `input[${attrName}="${fieldName}"][data-context-ref="${contextRef}"]:not([data-skip-validation]),` +
        ` select[${attrName}="${fieldName}"][data-context-ref="${contextRef}"]:not([data-skip-validation]),` +
        ` textarea[${attrName}="${fieldName}"][data-context-ref="${contextRef}"]:not([data-skip-validation])`
      );
      return el !== null;
    } else {
      // Check if field exists anywhere in form (exclude modal inputs)
      const el = document.querySelector(
        `input[${attrName}="${fieldName}"]:not([data-skip-validation]),` +
        ` select[${attrName}="${fieldName}"]:not([data-skip-validation]),` +
        ` textarea[${attrName}="${fieldName}"]:not([data-skip-validation])`
      );
      return el !== null;
    }
  }, []);

  // Build mapping: standardContext -> contextRef (one-time DOM scan, cached)
  const getContextMapping = useCallback((): Map<string, string> => {
    const now = Date.now();
    
    // Use cache if less than 1000ms old (context mapping rarely changes)
    if (contextMappingCacheRef.current && (now - contextMappingCacheRef.current.timestamp) < 1000) {
      return contextMappingCacheRef.current.mapping;
    }
    
    // One-time DOM scan to build mapping
    const mapping = new Map<string, string>();
    document.querySelectorAll('input[data-year-end][data-context-ref]').forEach(el => {
      const standardContext = el.getAttribute('data-year-end');
      const contextRef = el.getAttribute('data-context-ref');
      if (standardContext && contextRef) {
        mapping.set(standardContext, contextRef);
      }
    });
    
    contextMappingCacheRef.current = { mapping, timestamp: now };
    return mapping;
  }, []);

  // Get field value from ref using fieldName_standardContext format (for balance validation)
  const getFieldValueFromRef = useCallback((fieldName: string, standardContext: string): number => {
    // SPECIAL HANDLING: ClosingCash - calculate on-the-fly from DOM to avoid timing issues
    // Formula: ClosingCash = OpeningCash + IncreaseDecreaseInCashAndCashEquivalents
    if (fieldName === 'ClosingCash-CashAndCashEquivalents') {
      // Find OpeningCash in the same column (same data-year-end)
      const openingInput = document.querySelector(
        `input[name="OpeningCash-CashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      // Find IncreaseDecreaseInCashAndCashEquivalents in the same column (same data-year-end)
      const increaseDecreaseInput = document.querySelector(
        `input[name="IncreaseDecreaseInCashAndCashEquivalents"][data-year-end="${standardContext}"]`
      ) as HTMLInputElement;
      
      let openingCash = 0;
      let increaseDecrease = 0;
      
      if (openingInput && openingInput.value && openingInput.value.trim() !== '') {
        openingCash = parseFloat(openingInput.value.replace(/[^\d.-]/g, '')) || 0;
      }
      
      if (increaseDecreaseInput && increaseDecreaseInput.value && increaseDecreaseInput.value.trim() !== '') {
        increaseDecrease = parseFloat(increaseDecreaseInput.value.replace(/[^\d.-]/g, '')) || 0;
      }
      
      return openingCash + increaseDecrease;
    }
    
    if (!formDataRef?.current) {
      return 0;
    }
    
    // Ref stores in format: fieldName_standardContext (e.g., "Assets_current_SeparateMember")
    const key = `${fieldName}_${standardContext}`;
    const valueStr = formDataRef.current[key] || '';
    
    if (valueStr && valueStr.trim() !== '') {
      // Parse value (remove commas, handle formatting)
      const cleanValue = valueStr.replace(/,/g, '').replace(/[^\d.-]/g, '');
      const numericValue = parseFloat(cleanValue);
      return isNaN(numericValue) ? 0 : numericValue;
    }

    // Fallback for UI-only computed rows that are not persisted in ref
    const computedInput = document.querySelector(
      `input[name="${fieldName}"][data-year-end="${standardContext}"]`
    ) as HTMLInputElement | null;
    if (!computedInput || !computedInput.value || computedInput.value.trim() === '') {
      return 0;
    }
    const domClean = computedInput.value.replace(/,/g, '').replace(/[^\d.-]/g, '');
    const domNumeric = parseFloat(domClean);
    return isNaN(domNumeric) ? 0 : domNumeric;
  }, [formDataRef]);

  // Get SOCIE field value - special handler for SOCIE cross-validations
  // Handles: SOCIE-Equity, SOCIE-FundBalance, SOCIE-ProfitLoss, SOCIE-ComprehensiveIncome
  // SOCIE stores data in qname_contextRef format and uses special data attributes
  const getSOCIEFieldValue = useCallback((fieldName: string, standardContext: string): number => {
    // Extract member from standardContext (e.g., "current_SeparateMember" -> "SeparateMember")
    const memberMatch = standardContext.match(/(SeparateMember|ConsolidatedMember)$/);
    if (!memberMatch) {
      return 0;
    }
    const memberSuffix = memberMatch[1];
    const isCurrent = standardContext.startsWith('current_');
    
    // Map SOCIE field names to actual input field names and attributes
    // Note: equityMembers is an array to support both MPERS/MFRS (EquityMember) and CLBG (FundsAndReservesMember)
    const fieldConfig: Record<string, { 
      actualFieldName: string; 
      equityMembers: string[];  // Array to support multiple taxonomy types
      requireClosingAttr: boolean;
    }> = {
      'SOCIE-Equity': { 
        actualFieldName: 'Equity-Equity', 
        equityMembers: ['EquityMember'],
        requireClosingAttr: true  // Equity closing has data-equity-target="closing"
      },
      'SOCIE-FundBalance': { 
        actualFieldName: 'Equity-FundBalance', 
        equityMembers: ['FundsAndReservesMember'],
        requireClosingAttr: true
      },
      'SOCIE-ProfitLoss': { 
        actualFieldName: 'Equity-ProfitLoss', 
        equityMembers: ['EquityMember', 'FundsAndReservesMember'],  // MPERS/MFRS uses EquityMember, CLBG uses FundsAndReservesMember
        requireClosingAttr: false  // ProfitLoss doesn't have closing attr, just matches by member
      },
      'SOCIE-ComprehensiveIncome': { 
        actualFieldName: 'Equity-ComprehensiveIncome', 
        equityMembers: ['EquityMember', 'FundsAndReservesMember'],  // Support both for consistency
        requireClosingAttr: false
      }
    };
    
    const config = fieldConfig[fieldName];
    if (!config) {
      return 0;
    }
    
    // Build selectors for all possible equity members (to support both MPERS/MFRS and CLBG)
    const selectors = config.equityMembers.map(equityMember => {
      if (config.requireClosingAttr) {
        return `input[name="${config.actualFieldName}"][data-equity-target="closing"][data-equity-member="${equityMember}"]`;
      } else {
        return `input[name="${config.actualFieldName}"][data-equity-member="${equityMember}"]`;
      }
    });
    
    // Try each selector until we find matching inputs
    let matchingInputs: Element[] = [];
    for (const selector of selectors) {
      const inputs = document.querySelectorAll(selector);
      if (inputs.length > 0) {
        matchingInputs = Array.from(inputs);
        break;
      }
    }
    
    if (matchingInputs.length === 0) {
      return 0;
    }
    
    // Find the input that matches the context (current or comparative period)
    for (const input of Array.from(matchingInputs)) {
      const contextRef = input.getAttribute('data-context-ref') || '';
      
      // Check if this context includes the correct member suffix
      if (!contextRef.includes(memberSuffix)) {
        continue;
      }
      
      // Get the value from DOM (most accurate for SOCIE which uses its own HyperFormula)
      const domValue = (input as HTMLInputElement).value || '';
      if (domValue && domValue.trim() !== '') {
        const cleanValue = domValue.replace(/,/g, '').replace(/[^\d.-]/g, '');
        const numericValue = parseFloat(cleanValue);
        if (!isNaN(numericValue)) {
          // Determine if this is from current or comparative period table
          const table = input.closest('table.socie-table');
          const allTables = document.querySelectorAll(`table.socie-table[data-member-suffix="${memberSuffix}"]`);
          const tableIndex = Array.from(allTables).indexOf(table as Element);
          
          // Index 0 = current period, Index 1 = comparative period
          const tableIsCurrent = tableIndex === 0;
          
          if (tableIsCurrent === isCurrent) {
            return numericValue;
          }
        }
      }
    }
    
    return 0;
  }, []);

  // Get mandatory field value from ref using qname_contextRef format
  // For mandatory fields, we need to find the input/select to get qname and contextRef
  // Returns the first non-empty value found (checks all inputs/selects with this field name)
  const getMandatoryFieldValue = useCallback((fieldName: string): { value: string, contextRef: string | null } => {
    if (!formDataRef?.current) {
      return { value: '', contextRef: null };
    }
    
    const isQname = fieldName.includes(':');
    const attrName = isQname ? 'data-qname' : 'name';

    // Find ALL inputs/selects/textareas with this field name (in case there are multiple contexts)
    // IMPORTANT: Include select elements for enumeration fields
    const inputs = document.querySelectorAll(
      `input[${attrName}="${fieldName}"], select[${attrName}="${fieldName}"], textarea[${attrName}="${fieldName}"]`
    );
    
    if (inputs.length === 0) {
      console.warn(`⚠️ Validation: No input/select found for field: ${fieldName}`);
      return { value: '', contextRef: null };
    }
    
    // Check each input/select to find the first one with a value in the ref
    for (const input of Array.from(inputs)) {
      const qname = input.getAttribute('data-qname');
      const contextRef = input.getAttribute('data-context-ref');
      
      if (!qname || !contextRef) {
        console.warn(`⚠️ Validation: Missing qname or contextRef for field: ${fieldName}`, { qname, contextRef });
        continue;
      }
      
      // Ref stores in format: qname_contextRef (e.g., "ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated_fromto_20240701_20250630")
      const key = `${qname}_${contextRef}`;
      const value = formDataRef.current[key] || '';
      
      // For select elements, also check the DOM value as fallback (in case ref wasn't updated yet)
      if (!value || value.trim() === '') {
        const domValue = (input as HTMLInputElement | HTMLSelectElement).value || '';
        if (domValue && domValue.trim() !== '') {
          // Found value in DOM but not in ref - this shouldn't happen, but use DOM value as fallback
          console.warn(`⚠️ Validation: Found value in DOM but not in ref for ${fieldName}: ${domValue}`);
          return { value: domValue.trim(), contextRef };
        }
      }
      
      // Return the first non-empty value found
      if (value && value.trim() !== '') {
        return { value: value.trim(), contextRef };
      }
    }
    
    // If no input found or all values are empty, check if we can find at least one input to get contextRef
    // (for error reporting purposes)
    if (inputs.length > 0) {
      const firstInput = inputs[0];
      const qname = firstInput.getAttribute('data-qname');
      const contextRef = firstInput.getAttribute('data-context-ref');
      
      if (qname && contextRef) {
        return { value: '', contextRef }; // Return contextRef even if empty (for error reporting)
      }
    }
    
    return { value: '', contextRef: null };
  }, [formDataRef]);


  // Get all standard contexts from context mapping (which we build from DOM once)
  const getStandardContexts = useCallback((): string[] => {
    // Use the context mapping we already built - it has all standardContexts as keys
    const contextMapping = getContextMapping();
    return Array.from(contextMapping.keys());
  }, [getContextMapping]);

  // Check validation for all pairs and contexts - REF-BASED (much faster!)
  const checkValidation = useCallback(() => {
    if (!enabled) return;

    // 🚨 TEMPORARY: Skip validation if disabled
    if (VALIDATION_DISABLED) {
      devLog('⏸️ Validation DISABLED for performance testing');
      return;
    }
    
    // Skip validation if initial load is not complete (wait for specialFieldsCalculated event)
    if (!initialLoadCompleteRef.current) {
      devLog('⏸️ Validation: Skipping - initial load not complete yet');
      return;
    }
    
    if (!formDataRef?.current) {
      console.warn('⚠️ Validation: formDataRef not available');
      return;
    }
    
    const newErrors: ValidationError[] = [];
    const standardContexts = getStandardContexts();
    const contextMapping = getContextMapping();

    // 1. Check balance validation pairs (using module-level constant)
    standardContexts.forEach(standardContext => {
      VALIDATION_PAIRS.forEach((pair) => {
        // Check for SOCIE special fields
        const isSOCIETarget = pair.target.name.startsWith('SOCIE-');
        
        // CRITICAL: Check if BOTH fields exist in form (skip if either missing)
        // For SOCIE fields, check if the actual SOCIE input exists
        const sourceExists = fieldExistsInForm(pair.source.name);
        let targetExists: boolean;
        
        if (isSOCIETarget) {
          // For SOCIE targets, check if the actual SOCIE input exists
          // Map SOCIE field names to actual input attributes (equityMembers array supports both MPERS/MFRS and CLBG)
          const socieFieldMap: Record<string, { actualFieldName: string; equityMembers: string[]; requireClosingAttr: boolean }> = {
            'SOCIE-Equity': { actualFieldName: 'Equity-Equity', equityMembers: ['EquityMember'], requireClosingAttr: true },
            'SOCIE-FundBalance': { actualFieldName: 'Equity-FundBalance', equityMembers: ['FundsAndReservesMember'], requireClosingAttr: true },
            'SOCIE-ProfitLoss': { actualFieldName: 'Equity-ProfitLoss', equityMembers: ['EquityMember', 'FundsAndReservesMember'], requireClosingAttr: false },
            'SOCIE-ComprehensiveIncome': { actualFieldName: 'Equity-ComprehensiveIncome', equityMembers: ['EquityMember', 'FundsAndReservesMember'], requireClosingAttr: false }
          };
          
          const config = socieFieldMap[pair.target.name];
          if (config) {
            // Check for any of the possible equity members
            let socieInput: Element | null = null;
            for (const equityMember of config.equityMembers) {
              const selector = config.requireClosingAttr
                ? `input[name="${config.actualFieldName}"][data-equity-target="closing"][data-equity-member="${equityMember}"]`
                : `input[name="${config.actualFieldName}"][data-equity-member="${equityMember}"]`;
              socieInput = document.querySelector(selector);
              if (socieInput) break;
            }
            targetExists = socieInput !== null;
          } else {
            targetExists = false;
          }
        } else {
          targetExists = fieldExistsInForm(pair.target.name);
        }
        
        if (!sourceExists || !targetExists) {
          // Skip this pair - field(s) not in this taxonomy
          return; // Continue to next pair
        }
        
        // Get values - use special handler for SOCIE fields
        const sourceValue = getFieldValueFromRef(pair.source.name, standardContext);
        const targetValue = isSOCIETarget 
          ? getSOCIEFieldValue(pair.target.name, standardContext)
          : getFieldValueFromRef(pair.target.name, standardContext);
        
        // Early exit optimization: skip if both are 0 (no validation needed)
        if (sourceValue === 0 && targetValue === 0) {
          return;
        }
        
        const difference = Math.abs(sourceValue - targetValue);

        // Only flag as error if both fields have values and they don't match
        if ((sourceValue !== 0 || targetValue !== 0) && difference > 0.01) { // 0.01 tolerance for rounding
          // CRITICAL: contextRef must match the actual field's data-context-ref.
          // Using the cached mapping can pick an "asof_*" context even when the field is "fromto_*" (duration),
          // which breaks UI matching for ProfitLoss / ComprehensiveIncome pairs.
          const getContextRefFromDom = (fieldName: string): string | null => {
            const el = document.querySelector(
              `input[name="${fieldName}"][data-year-end="${standardContext}"]:not([data-skip-validation])`
            ) as HTMLInputElement | null;
            return el?.getAttribute('data-context-ref') || null;
          };
          
          // Special handler for SOCIE context ref lookup
          const getSOCIEContextRefFromDom = (fieldName: string): string | null => {
            // Map SOCIE field names to actual input attributes (equityMembers array supports both MPERS/MFRS and CLBG)
            const socieFieldMap: Record<string, { actualFieldName: string; equityMembers: string[]; requireClosingAttr: boolean }> = {
              'SOCIE-Equity': { actualFieldName: 'Equity-Equity', equityMembers: ['EquityMember'], requireClosingAttr: true },
              'SOCIE-FundBalance': { actualFieldName: 'Equity-FundBalance', equityMembers: ['FundsAndReservesMember'], requireClosingAttr: true },
              'SOCIE-ProfitLoss': { actualFieldName: 'Equity-ProfitLoss', equityMembers: ['EquityMember', 'FundsAndReservesMember'], requireClosingAttr: false },
              'SOCIE-ComprehensiveIncome': { actualFieldName: 'Equity-ComprehensiveIncome', equityMembers: ['EquityMember', 'FundsAndReservesMember'], requireClosingAttr: false }
            };
            
            const config = socieFieldMap[fieldName];
            if (!config) return null;
            
            const memberMatch = standardContext.match(/(SeparateMember|ConsolidatedMember)$/);
            if (!memberMatch) return null;
            
            const memberSuffix = memberMatch[1];
            const isCurrent = standardContext.startsWith('current_');
            
            // Try each possible equity member until we find a match
            for (const equityMember of config.equityMembers) {
              const selector = config.requireClosingAttr
                ? `input[name="${config.actualFieldName}"][data-equity-target="closing"][data-equity-member="${equityMember}"]`
                : `input[name="${config.actualFieldName}"][data-equity-member="${equityMember}"]`;
              
              const matchingInputs = document.querySelectorAll(selector);
              
              for (const input of Array.from(matchingInputs)) {
                const contextRef = input.getAttribute('data-context-ref') || '';
                if (!contextRef.includes(memberSuffix)) continue;
                
                // Determine if this is current or comparative period based on table index
                const table = input.closest('table.socie-table');
                const allTables = document.querySelectorAll(`table.socie-table[data-member-suffix="${memberSuffix}"]`);
                const tableIndex = Array.from(allTables).indexOf(table as Element);
                const tableIsCurrent = tableIndex === 0;
                
                if (tableIsCurrent === isCurrent) {
                  return contextRef;
                }
              }
            }
            return null;
          };

          const contextRef = isSOCIETarget
            ? (getContextRefFromDom(pair.source.name) || getSOCIEContextRefFromDom(pair.target.name) || contextMapping.get(standardContext) || standardContext)
            : (getContextRefFromDom(pair.source.name) || getContextRefFromDom(pair.target.name) || contextMapping.get(standardContext) || standardContext);
          
          // Use human-readable names for SOCIE errors
          const socieDisplayNames: Record<string, string> = {
            'SOCIE-Equity': 'SOCIE Closing Equity',
            'SOCIE-FundBalance': 'SOCIE Closing Fund Balance',
            'SOCIE-ProfitLoss': 'SOCIE Profit/Loss',
            'SOCIE-ComprehensiveIncome': 'SOCIE Comprehensive Income'
          };
          const targetDisplayName = isSOCIETarget 
            ? (socieDisplayNames[pair.target.name] || pair.target.name)
            : pair.target.name;
          const sourceDisplayName = pair.source.name === 'FundAndReserve' ? 'SOFP Fund & Reserve' : 
            pair.source.name === 'Equity' && isSOCIETarget ? 'SOFP Equity' : pair.source.name;
          
          const error: ValidationError = {
            id: `${pair.source.name}_${pair.target.name}_${contextRef}`,
            source: pair.source.name,
            target: pair.target.name,
            sourceValue,
            targetValue,
            difference,
            contextRef,
            message: `${sourceDisplayName} (${sourceValue.toLocaleString()}) must equal ${targetDisplayName} (${targetValue.toLocaleString()})`,
            type: 'balance'
          };
          
          devLog(`🚨 Validation error found: ${error.source} (${sourceValue}) vs ${error.target} (${targetValue}) in ${contextRef}`);
          newErrors.push(error);
        }
      });
    });

    // 1b. SG PPE/IA root-member direct pair checks (SOCIE-style special targeting)
    const addRootMemberPairError = (
      sectionId: string,
      sourceName: string,
      targetName: string,
      rootMember: string,
      sourceLabel: string,
      targetLabel: string,
      sourceSelectorSuffix: string = ''
    ) => {
      const sectionEl = document.getElementById(sectionId);
      if (!sectionEl) return;

      const sourceInputs = Array.from(
        sectionEl.querySelectorAll(
          `input[name="${sourceName}"][data-equity-member="${rootMember}"]${sourceSelectorSuffix}`
        )
      ) as HTMLInputElement[];

      sourceInputs.forEach((sourceInput) => {
        const contextRef = sourceInput.getAttribute('data-context-ref') || '';
        if (!contextRef) return;

        const targetInput = sectionEl.querySelector(
          `input[name="${targetName}"][data-equity-member="${rootMember}"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;
        if (!targetInput) return;

        const sourceValue = parseFloat((sourceInput.value || '0').replace(/[^\d.-]/g, '')) || 0;
        const targetValue = parseFloat((targetInput.value || '0').replace(/[^\d.-]/g, '')) || 0;

        if (sourceValue === 0 && targetValue === 0) return;
        const difference = Math.abs(sourceValue - targetValue);
        if (difference <= 0.01) return;

        newErrors.push({
          id: `${sourceName}_${targetName}_${contextRef}`,
          source: sourceName,
          target: targetName,
          sourceValue,
          targetValue,
          difference,
          contextRef,
          message: `${sourceLabel} (${sourceValue.toLocaleString()}) must equal ${targetLabel} (${targetValue.toLocaleString()})`,
          type: 'balance'
        });
      });
    };

    addRootMemberPairError(
      '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract',
      'PPE-PropertyPlantAndEquipment',
      'PPE-ComputedAtCostAndValuationTotal',
      'PropertyPlantAndEquipmentMember',
      'PPE total',
      'PPE At cost + At valuation (computed total)',
      '[data-nbv-closing="true"]'
    );

    addRootMemberPairError(
      '33000000_DisclosureOfIntangibleAssetsAbstract',
      'IA-IntangibleAssetsOtherThanGoodwill',
      'IA-ComputedAtCostAndValuationTotal',
      'IntangibleAssetsMember',
      'IA total',
      'IA At cost + At valuation (computed total)',
      '[data-nbv-closing="true"]'
    );

    // 1d. SG cross-section PPE check (SOFP <-> PPE note)
    // Indicator should appear on SOFP side; still keep relation to PPE note via target.
    const ppeSectionEl = document.getElementById('32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract');
    const sofpSectionEl = document.getElementById('21100000_StatementOfFinancialPositionAbstract');
    if (ppeSectionEl && sofpSectionEl) {
      const ppeNoteInputs = Array.from(
        ppeSectionEl.querySelectorAll(
          `input[name="PPE-PropertyPlantAndEquipment"][data-equity-member="PropertyPlantAndEquipmentMember"][data-nbv-closing="true"]`
        )
      ) as HTMLInputElement[];

      devLog(`[PPE-XCHECK] Found ${ppeNoteInputs.length} PPE note root input(s) for SOFP cross-check`);

      ppeNoteInputs.forEach((ppeInput) => {
        const contextRef = ppeInput.getAttribute('data-context-ref') || '';
        if (!contextRef) {
          devLog('[PPE-XCHECK] Skip input: missing data-context-ref');
          return;
        }

        const sofpInput = sofpSectionEl.querySelector(
          `input[name="PropertyPlantAndEquipment"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;
        if (!sofpInput) {
          devLog(`[PPE-XCHECK] No SOFP match for context=${contextRef}`);
          return;
        }

        const sofpValue = parseFloat((sofpInput.value || '0').replace(/[^\d.-]/g, '')) || 0;
        const ppeValue = parseFloat((ppeInput.value || '0').replace(/[^\d.-]/g, '')) || 0;
        devLog(`[PPE-XCHECK] context=${contextRef} SOFP=${sofpValue} PPE_NOTE=${ppeValue}`);
        if (sofpValue === 0 && ppeValue === 0) {
          devLog(`[PPE-XCHECK] Skip context=${contextRef}: both values are 0`);
          return;
        }

        const difference = Math.abs(sofpValue - ppeValue);
        if (difference <= 0.01) {
          devLog(`[PPE-XCHECK] Pass context=${contextRef}: difference=${difference}`);
          return;
        }

        const ppeCrossError: ValidationError = {
          id: `sofp_ppe_match_${contextRef}`,
          source: 'PropertyPlantAndEquipment',
          target: 'PPE-PropertyPlantAndEquipment',
          sourceValue: sofpValue,
          targetValue: ppeValue,
          difference,
          contextRef,
          message: `SOFP PPE (${sofpValue.toLocaleString()}) must equal PPE note total (${ppeValue.toLocaleString()})`,
          type: 'balance'
        };

        devLog(
          `[PPE-XCHECK] ERROR id=${ppeCrossError.id} context=${contextRef} SOFP=${sofpValue} PPE_NOTE=${ppeValue} diff=${difference}`
        );
        newErrors.push(ppeCrossError);
      });
    }

    // 1e. SG cross-section IA check (SOFP <-> IA note)
    // Indicator should appear on SOFP side; keep relation to IA note via target.
    const iaSectionEl = document.getElementById('33000000_DisclosureOfIntangibleAssetsAbstract');
    if (iaSectionEl && sofpSectionEl) {
      const iaNoteInputs = Array.from(
        iaSectionEl.querySelectorAll(
          `input[name="IA-IntangibleAssetsOtherThanGoodwill"][data-equity-member="IntangibleAssetsMember"][data-nbv-closing="true"]`
        )
      ) as HTMLInputElement[];

      iaNoteInputs.forEach((iaInput) => {
        const contextRef = iaInput.getAttribute('data-context-ref') || '';
        if (!contextRef) return;

        const sofpInput = sofpSectionEl.querySelector(
          `input[name="IntangibleAssetsOtherThanGoodwill"][data-context-ref="${contextRef}"]`
        ) as HTMLInputElement | null;
        if (!sofpInput) return;

        const sofpValue = parseFloat((sofpInput.value || '0').replace(/[^\d.-]/g, '')) || 0;
        const iaValue = parseFloat((iaInput.value || '0').replace(/[^\d.-]/g, '')) || 0;
        if (sofpValue === 0 && iaValue === 0) return;

        const difference = Math.abs(sofpValue - iaValue);
        if (difference <= 0.01) return;

        newErrors.push({
          id: `sofp_ia_match_${contextRef}`,
          source: 'IntangibleAssetsOtherThanGoodwill',
          target: 'IA-IntangibleAssetsOtherThanGoodwill',
          sourceValue: sofpValue,
          targetValue: iaValue,
          difference,
          contextRef,
          message: `SOFP IA (${sofpValue.toLocaleString()}) must equal IA note total (${iaValue.toLocaleString()})`,
          type: 'balance'
        });
      });
    }

    // 2. Check mandatory fields (using module-level constant)
    MANDATORY_FIELDS.forEach(fieldName => {
      // CRITICAL: Check if field exists in form first (skip if not in taxonomy)
      if (!fieldExistsInForm(fieldName)) {
        return; // Skip this mandatory field - not in this taxonomy
      }
      
      const { value, contextRef } = getMandatoryFieldValue(fieldName);
      
      // Check if field is empty or not filled
      if (!value || value.trim() === '') {
        const error: ValidationError = {
          id: `mandatory_${fieldName}_${contextRef || 'unknown'}`,
          source: fieldName,
          target: '',
          sourceValue: 0,
          targetValue: 0,
          difference: 0,
          contextRef: contextRef || 'unknown',
          message: `This field cannot be empty`,
          type: 'mandatory',
          fieldName
        };
        
        devLog(`🚨 Mandatory field error: ${fieldName} is not filled`);
        newErrors.push(error);
      }
    });

    // 3. SG conditional checks (phase 2, non-calculation)
    const auditOpinionField = 'TypeOfAuditOpinionInIndependentAuditorsReport';
    if (fieldExistsInForm(auditOpinionField)) {
      const { value: auditOpinion, contextRef: auditContextRef } = getMandatoryFieldValue(auditOpinionField);
      const normalizedAuditOpinion = (auditOpinion || '').trim().toLowerCase();
      // "Unqualified (clean)" and "Not audited" do not need the modified-report reason fields.
      // All others (emphasis of matter, qualified, adverse, disclaimer) require at least one reason.
      const noReasonFieldsNeeded =
        normalizedAuditOpinion.includes('(clean)') || normalizedAuditOpinion.includes('not audited');
      const isModifiedOpinion = normalizedAuditOpinion !== '' && !noReasonFieldsNeeded;
      if (isModifiedOpinion) {
        const existingReasonFields = SG_AUDIT_MODIFIED_REASON_FIELDS.filter((fieldName) => fieldExistsInForm(fieldName));
        const hasAnyReasonFilled = existingReasonFields.some((reasonField) => {
          const { value } = getMandatoryFieldValue(reasonField);
          return !!value && value.trim() !== '';
        });

        if (!hasAnyReasonFilled) {
          existingReasonFields.forEach((reasonField) => {
            newErrors.push({
              id: `conditional_${auditOpinionField}_${reasonField}_${auditContextRef || 'unknown'}`,
              source: reasonField,
              target: auditOpinionField,
              sourceValue: 0,
              targetValue: 0,
              difference: 0,
              contextRef: auditContextRef || 'unknown',
              message: 'At least one modified audit opinion reason is required when audit opinion is not unmodified.',
              type: 'conditional',
              fieldName: reasonField,
            });
          });
        }
      }
    }

    // 4. Phase 2 keeps MY-specific format checks disabled.

    // Store errors in REF (no React state update!)
    errorsRef.current = newErrors;
    
    // Update DOM directly (like 1.0) - add/remove error indicators and classes
    updateDOMWithErrors(newErrors);
    
    // Dispatch event so badges can update (FormSectionTabs listens to this)
    document.dispatchEvent(new CustomEvent('validationUpdated', { detail: { errorCount: newErrors.length } }));
    
    // Log validation results
    const balanceErrors = newErrors.filter(e => e.type === 'balance').length;
    const mandatoryErrors = newErrors.filter(e => e.type === 'mandatory').length;
    const conditionalErrors = newErrors.filter(e => e.type === 'conditional').length;
    const formatErrors = newErrors.filter(e => e.type === 'format').length;
    
    if (newErrors.length > 0) {
      devLog(`⚠️ Validation: ${balanceErrors} balance errors, ${mandatoryErrors} mandatory field errors, ${conditionalErrors} conditional field errors, ${formatErrors} format errors found`);
    } else {
      devLog('✅ Validation: All checks passed');
    }
  // Note: updateDOMWithErrors is used inside but defined below - React handles this via closure
  }, [enabled, formDataRef, getFieldValueFromRef, getSOCIEFieldValue, getStandardContexts, getContextMapping, getMandatoryFieldValue, fieldExistsInForm]);

  // Listen to DOM events - BLUR-based validation (no debounce needed)
  useEffect(() => {
    if (!enabled) return;

    // 🚨 TEMPORARY: Skip event listeners if validation disabled
    if (VALIDATION_DISABLED) {
      devLog('⏸️ Validation event listeners DISABLED for performance testing');
      return;
    }
    
    const handleInputChange = (e: Event) => {
      // Don't run validation during initial load
      if (!initialLoadCompleteRef.current) {
        return;
      }
      
      const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      
      // 🔒 SAFETY: Ensure target exists and is a valid element
      if (!target || typeof target.getAttribute !== 'function') {
        return;
      }
      
      // Skip validation for inputs marked with data-skip-validation="true"
      if (target.getAttribute('data-skip-validation') === 'true') {
        return;
      }
      
      // 🔑 PERF: Skip validation if value didn't actually change
      const fieldName = target.name;
      const contextRef = target.getAttribute('data-context-ref') || '';
      const key = `${fieldName}_${contextRef}`;
      const currentValue = target.value || '';
      
      if (lastValueMapRef.current.get(key) === currentValue) {
        return; // 🚫 No real change - skip validation
      }
      lastValueMapRef.current.set(key, currentValue);
      
      // Run validation with small delay to ensure formDataRef is updated first
      // (SOFPTable's handleBlur calls onFieldChange which updates formDataRef)
      if (target.classList?.contains('financial-input') || 
          target.tagName === 'INPUT' || 
          target.tagName === 'SELECT' || 
          target.tagName === 'TEXTAREA') {
        setTimeout(() => checkValidation(), 50);
      }
    };

    const handleHyperFormulaUpdate = () => {
      if (!initialLoadCompleteRef.current) return;
      checkValidation();
    };

    const handleFieldUpdated = () => {
      if (!initialLoadCompleteRef.current) return;
      checkValidation();
    };

    // Footnote updates can trigger immediate validation before UI-only computed/mirror rows
    // finish their deferred DOM recompute in SOFPTableSG. Re-validate slightly later.
    const handleFootnoteValueUpdated = () => {
      if (!initialLoadCompleteRef.current) return;
      setTimeout(() => checkValidation(), 90);
    };

    const handleSpecialFieldsCalculated = () => {
      if (initialLoadCompleteRef.current) return;
      
      initialLoadCompleteRef.current = true;
      devLog('✅ Validation: Initial load complete, validation is now enabled');
      
      // Run initial validation after everything is ready
      setTimeout(() => {
        devLog('📥 Validation: Running initial validation after load complete...');
        checkValidation();
      }, 200);
    };

    // Handle SOCIE calculations complete - validate after SOCIE HyperFormula finishes
    const handleSOCIECalculationsComplete = () => {
      if (!initialLoadCompleteRef.current) return;
      devLog('📊 Validation: SOCIE calculations complete, re-validating...');
      checkValidation();
    };

    // BLUR-based validation - fires once per field (no keystroke lag)
    // Note: blur doesn't bubble, so we use capture phase (true) to catch it at document level.
    // The 50ms delay above ensures formDataRef is updated before validation reads it.
    document.addEventListener('blur', handleInputChange, true); // Capture phase required for blur
    document.addEventListener('change', handleInputChange); // Instant for dropdowns/selects
    document.addEventListener('hyperformulaUpdate', handleHyperFormulaUpdate);
    document.addEventListener('fieldUpdated', handleFieldUpdated);
    document.addEventListener('validationNeeded', handleFieldUpdated);
    document.addEventListener('footnoteValueUpdated', handleFootnoteValueUpdated);
    document.addEventListener('specialFieldsCalculated', handleSpecialFieldsCalculated);
    document.addEventListener('socieCalculationsComplete', handleSOCIECalculationsComplete);

    return () => {
      document.removeEventListener('blur', handleInputChange, true);
      document.removeEventListener('change', handleInputChange);
      document.removeEventListener('hyperformulaUpdate', handleHyperFormulaUpdate);
      document.removeEventListener('fieldUpdated', handleFieldUpdated);
      document.removeEventListener('validationNeeded', handleFieldUpdated);
      document.removeEventListener('footnoteValueUpdated', handleFootnoteValueUpdated);
      document.removeEventListener('specialFieldsCalculated', handleSpecialFieldsCalculated);
      document.removeEventListener('socieCalculationsComplete', handleSOCIECalculationsComplete);
    };
  }, [checkValidation, enabled]);

  // ✅ SAFE UPSERT: Show/hide pre-rendered ValidationIndicator + add .has-error class
  // Uses UPSERT pattern - no appendChild, just toggle visibility
  const updateDOMWithErrors = useCallback((newErrors: ValidationError[]) => {
    // 🔒 CRITICAL: Prevent concurrent updates (race condition guard)
    if (isUpdatingDOMRef.current) {
      devLog('⚠️ Validation: Already updating DOM, skipping to prevent race condition');
      return;
    }
    
    // Wait for React to finish (give commit phase + layout + paint time to complete)
    setTimeout(() => {
      isUpdatingDOMRef.current = true;
      
      try {
        // 1. HIDE all existing error indicators (UPSERT - don't remove!)
        // ✅ SAFE: Indicators stay in tree, just hidden
        document.querySelectorAll('.validation-error-indicator').forEach(indicator => {
          try {
            (indicator as HTMLElement).style.display = 'none';
            // Also hide any tooltips
            const tooltip = indicator.querySelector('.validation-error-tooltip-hover') as HTMLElement;
            if (tooltip) {
              tooltip.style.display = 'none';
            }
          } catch (e) {
            // Ignore
          }
        });
        
        // 2. Clear all has-error classes and reset padding
        document.querySelectorAll('.has-error').forEach(el => {
          try {
            el.classList.remove('has-error');
            // CRITICAL: Reset padding when error is cleared
            if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
              el.style.paddingRight = '';
            }
          } catch (e) {
            // Ignore if element was removed by React
          }
        });
        
        // 3. Add error indicators and classes for each error
        newErrors.forEach(error => {
          let targetElements: (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[] = [];
          
          if (error.type === 'balance') {
            // For balance errors, mark BOTH source and target fields
            const isPPERootTotalPair = error.source === 'PPE-PropertyPlantAndEquipment' &&
              error.target === 'PPE-ComputedAtCostAndValuationTotal';
            const isIARootTotalPair = error.source === 'IA-IntangibleAssetsOtherThanGoodwill' &&
              error.target === 'IA-ComputedAtCostAndValuationTotal';
            const isSOFPPPECrossSection = error.source === 'PropertyPlantAndEquipment' &&
              error.target === 'PPE-PropertyPlantAndEquipment';
            const isSOFPIACrossSection = error.source === 'IntangibleAssetsOtherThanGoodwill' &&
              error.target === 'IA-IntangibleAssetsOtherThanGoodwill';

            const sourceSelector = (isPPERootTotalPair || isIARootTotalPair)
              ? `input[name="${error.source}"][data-context-ref="${error.contextRef}"][data-nbv-closing="true"]`
              : `input[name="${error.source}"][data-context-ref="${error.contextRef}"]`;

            const sourceInputs = document.querySelectorAll(sourceSelector) as NodeListOf<HTMLInputElement>;
            
            // Handle SOCIE target fields specially - they use different naming
            let targetInputs: NodeListOf<HTMLInputElement>;
            if (error.target.startsWith('SOCIE-')) {
              // Map SOCIE field names to actual input field names
              const socieFieldMap: Record<string, { actualFieldName: string; requireClosingAttr: boolean }> = {
                'SOCIE-Equity': { actualFieldName: 'Equity-Equity', requireClosingAttr: true },
                'SOCIE-FundBalance': { actualFieldName: 'Equity-FundBalance', requireClosingAttr: true },
                'SOCIE-ProfitLoss': { actualFieldName: 'Equity-ProfitLoss', requireClosingAttr: false },
                'SOCIE-ComprehensiveIncome': { actualFieldName: 'Equity-ComprehensiveIncome', requireClosingAttr: false }
              };
              
              const config = socieFieldMap[error.target];
              if (config) {
                const selector = config.requireClosingAttr
                  ? `input[name="${config.actualFieldName}"][data-context-ref="${error.contextRef}"][data-equity-target="closing"]`
                  : `input[name="${config.actualFieldName}"][data-context-ref="${error.contextRef}"]`;
                targetInputs = document.querySelectorAll(selector) as NodeListOf<HTMLInputElement>;
              } else {
                targetInputs = document.querySelectorAll(
                  `input[name="${error.target}"][data-context-ref="${error.contextRef}"]`
                ) as NodeListOf<HTMLInputElement>;
              }
            } else {
              targetInputs = document.querySelectorAll(
                `input[name="${error.target}"][data-context-ref="${error.contextRef}"]`
              ) as NodeListOf<HTMLInputElement>;
            }
            
            targetElements = (isSOFPPPECrossSection || isSOFPIACrossSection)
              ? [...Array.from(sourceInputs)] // SOFP side only
              : [...Array.from(sourceInputs), ...Array.from(targetInputs)];
          } else {
            // For mandatory/conditional/format errors, only mark the field itself
            const fieldName = error.fieldName || error.source;
            const elements = document.querySelectorAll(
              `input[name="${fieldName}"][data-context-ref="${error.contextRef}"], ` +
              `select[name="${fieldName}"][data-context-ref="${error.contextRef}"], ` +
              `textarea[name="${fieldName}"][data-context-ref="${error.contextRef}"]`
            ) as NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
            
            targetElements = Array.from(elements);
          }
          
          targetElements.forEach(element => {
            // ✅ SAFETY: Only if element is currently mounted
            if (!element.isConnected) {
              return;
            }
            
            // Add has-error class for red border
            element.classList.add('has-error');
            
            // ✅ SPECIAL CASE: Hidden textarea with visible text-block-input-group
            if (element.tagName === 'TEXTAREA' && element.classList.contains('hidden')) {
              const textBlockGroup = element.parentElement?.querySelector('.text-block-input-group') as HTMLElement;
              if (textBlockGroup) {
                textBlockGroup.classList.add('has-error');
              }
            }
            
            // Find parent wrapper (must already exist with position: relative)
            const wrapper = element.parentElement;
            if (!wrapper) {
              return;
            }
            
            // 🔒 SAFETY GUARD A: Don't append to detached nodes
            if (!wrapper.isConnected) {
              return;
            }
            
            // Ensure parent has position: relative for absolute indicator
            const computedPosition = window.getComputedStyle(wrapper).position;
            if (computedPosition === 'static') {
              wrapper.style.position = 'relative';
            }
            
            // ✅ FIND pre-rendered indicator (React created it!)
            const indicator = wrapper.querySelector('.validation-error-indicator') as HTMLElement;
            
            if (!indicator) {
              // No indicator pre-rendered - skip (React should have created it in FormField)
              return;
            }
            
            // ✅ SAFE: Just toggle visibility and update message (NO appendChild!)
            indicator.style.display = 'flex';
            indicator.setAttribute('data-error-message', error.message);
            // Note: Don't set title attribute - custom tooltip handles it
            
            // CRITICAL: Add padding to make room for indicator
            if (element.tagName === 'INPUT' || element.tagName === 'SELECT') {
              element.style.paddingRight = '32px';
            }
          });
        });
        
      } finally {
        // 🔒 Release lock after DOM updates complete
        isUpdatingDOMRef.current = false;
      }
    }, 0); // 0ms delay to wait for next event loop tick after React finishes
  }, []);

  const clearErrors = useCallback(() => {
    errorsRef.current = [];
    
    // ✅ UPSERT: Just HIDE indicators and clear classes (don't remove!)
    setTimeout(() => {
      // Hide all validation indicators
      document.querySelectorAll('.validation-error-indicator').forEach(indicator => {
        try {
          (indicator as HTMLElement).style.display = 'none';
          // Hide any tooltips
          const tooltip = indicator.querySelector('.validation-error-tooltip-hover') as HTMLElement;
          if (tooltip) {
            tooltip.style.display = 'none';
          }
        } catch (e) {
          // Ignore
        }
      });
      
      // Clear has-error classes and reset padding
      document.querySelectorAll('.has-error').forEach(el => {
        try {
          el.classList.remove('has-error');
          // Reset padding when error is cleared
          if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
            el.style.paddingRight = '';
          }
        } catch (e) {
          // Ignore if element was removed
        }
      });
      
      // Also clear from text-block-input-group wrappers
      document.querySelectorAll('.text-block-input-group.has-error').forEach(el => {
        try {
          el.classList.remove('has-error');
        } catch (e) {
          // Ignore
        }
      });
      
      // Dispatch event so badges can update
      document.dispatchEvent(new CustomEvent('validationUpdated', { detail: { errorCount: 0 } }));
    }, 0);
    
    // Clear section badges (keeping this for backwards compatibility)
    if (typeof window !== 'undefined') {
      document.querySelectorAll('.btn-sub-panel[data-tab]').forEach(button => {
        button.removeAttribute('data-error-count');
      });
    }
    
    // Dispatch empty errors for footer
    document.dispatchEvent(new CustomEvent('validationComplete', {
      detail: { errors: [] }
    }));
  }, []);

  // Get errors for a specific section (read from ref, not state)
  const getSectionErrors = useCallback((sectionCode: string): ValidationError[] => {
    // Simple mapping: only show errors where the section owns the fields
    const sectionFieldPatterns: Record<string, string[]> = {
      // SG SOFP section
      '21100000_StatementOfFinancialPositionAbstract': [
        'Assets',
        'EquityAndLiabilities',
        'FundAndLiabilities',
        'Computed-EquityAndLiabilitiesTotal',
        'PropertyPlantAndEquipment',
        'IntangibleAssetsOtherThanGoodwill',
      ],

      // SG SOPL section
      '22000000_StatementOfProfitOrLossAbstract': [
        'ProfitLoss',
        'SOPL-ProfitLoss',
        'Computed-ProfitLossAttributableTotal',
      ],

      // SG PPE note section
      '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract': [
        'PPE-PropertyPlantAndEquipment',
        'PPE-ComputedAtCostAndValuationTotal',
      ],

      // SG IA note section
      '33000000_DisclosureOfIntangibleAssetsAbstract': [
        'IA-IntangibleAssetsOtherThanGoodwill',
        'IA-ComputedAtCostAndValuationTotal',
      ],

      // SG analysis sections (same-page direct pair checks)
      '31000000_TradeAndOtherReceivablesAbstract': [
        'TradeAndOtherReceivables',
        'Computed-TradeAndOtherReceivablesTotal',
      ],
      '35000000_TradeAndOtherPayablesAbstract': [
        'TradeAndOtherPayables',
        'Computed-TradeAndOtherPayablesTotal',
      ],
      '36000000_DisclosureOfLoansAndBorrowingsAbstract': [
        'LoansAndBorrowings',
        'Computed-LoansAndBorrowingsTotal',
      ],

      // Balance Sheet sections - show balance errors
      '200100x_DisclosureOnStatementOfFinancialPositionAbstract': ['Assets', 'EquityAndLiabilities', 'FundAndLiabilities'],
      '200100y_DisclosureOnStatementOfFinancialPositionAbstract': ['Assets', 'EquityAndLiabilities', 'FundAndLiabilities'],
      
      // SOPL main sections - show ProfitLoss vs SOPL-ProfitLoss errors ONLY
      '300100x_DisclosureOnStatementOfProfitOrLossAbstract': ['ProfitLoss', 'SOPL-ProfitLoss'],
      '300100y_DisclosureOnStatementOfProfitOrLossAbstract': ['ProfitLoss', 'SOPL-ProfitLoss'],
      
      // SOCI sections - show ComprehensiveIncome vs SOCI-ComprehensiveIncome errors
      '400100x_DisclosureOnStatementOfComprehensiveIncomeNetOfTaxAbstract': ['ComprehensiveIncome', 'SOCI-ComprehensiveIncome'],
      
      // CLBG SOIE sections - show BOTH ProfitLoss AND ComprehensiveIncome pairs
      '300100x_DisclosureOnStatementOfIncomeAndExpenditureAbstract': ['ProfitLoss', 'SOPL-ProfitLoss', 'ComprehensiveIncome', 'SOCI-ComprehensiveIncome'],
      '300100y_DisclosureOnStatementOfIncomeAndExpenditureAbstract': ['ProfitLoss', 'SOPL-ProfitLoss', 'ComprehensiveIncome', 'SOCI-ComprehensiveIncome'],

      // Cash Flow sections  - show CashAndCashEquivalents vs ClosingCash-CashAndCashEquivalents errors
      '500100x_DisclosureOnStatementOfCashFlowsAbstract': ['CashAndCashEquivalents', 'ClosingCash-CashAndCashEquivalents'],
      '500100y_DisclosureOnStatementOfCashFlowsAbstract': ['CashAndCashEquivalents', 'ClosingCash-CashAndCashEquivalents'],
      
      // SOCIE sections - show SOCIE cross-validation errors (MPERS/MFRS)
      // Includes: Equity and ProfitLoss cross-checks
      '610000_DisclosureOfChangesInEquityAbstract': [
        'Equity', 'SOCIE-Equity',
        'ProfitLoss', 'SOCIE-ProfitLoss'
      ],
      
      // SOCIE sections - show FundAndReserve vs SOCIE-FundBalance cross-validation errors (CLBG)
      '610000_DisclosureOnStatementOfChangesInFundAbstract': [
        'FundAndReserve', 'SOCIE-FundBalance',
        'ProfitLoss', 'SOCIE-ProfitLoss'
      ],
      
      // SOPL Detail sections - NO errors shown (changes affect main SOPL only)
      // '300200x_DisclosureOnStatementOfProfitOrLossAbstract': [], // No badge for detail section
      // '300200y_DisclosureOnStatementOfProfitOrLossAbstract': [], // No badge for detail section
    };

    const sectionMandatoryFields = SG_SECTION_MANDATORY_FIELDS;

    const fieldPatterns = sectionFieldPatterns[sectionCode] || [];
    const mandatoryFieldsForSection = sectionMandatoryFields[sectionCode] || [];
    
    // If no patterns defined for this section, check for mandatory fields only
    if (fieldPatterns.length === 0 && mandatoryFieldsForSection.length === 0) {
      return [];
    }
    
    // Filter errors from ref (not state)
    return errorsRef.current.filter(error => {
      // Check balance errors
      if (error.type === 'balance' && fieldPatterns.length > 0) {
        // SG PPE/IA section ownership rules (strict):
        // 1) PPE note pair => PPE note section only
        // 2) IA note pair => IA note section only
        // 3) SOFP<->PPE/IA cross-check => SOFP section only
        const isPPERootTotalPair =
          error.source === 'PPE-PropertyPlantAndEquipment' &&
          error.target === 'PPE-ComputedAtCostAndValuationTotal';
        const isIARootTotalPair =
          error.source === 'IA-IntangibleAssetsOtherThanGoodwill' &&
          error.target === 'IA-ComputedAtCostAndValuationTotal';
        const isSOFPPPECrossSection =
          error.source === 'PropertyPlantAndEquipment' &&
          error.target === 'PPE-PropertyPlantAndEquipment';
        const isSOFPIACrossSection =
          error.source === 'IntangibleAssetsOtherThanGoodwill' &&
          error.target === 'IA-IntangibleAssetsOtherThanGoodwill';
        if (isPPERootTotalPair) return sectionCode === '32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract';
        if (isIARootTotalPair) return sectionCode === '33000000_DisclosureOfIntangibleAssetsAbstract';
        if (isSOFPPPECrossSection || isSOFPIACrossSection) return sectionCode === '21100000_StatementOfFinancialPositionAbstract';

        // SOCIE-specific errors (target starts with SOCIE-) should ONLY appear in SOCIE sections
        const isSOCIETargetError = error.target.startsWith('SOCIE-');
        const isSOCIESection = sectionCode.startsWith('610');
        
        if (isSOCIETargetError) {
          // SOCIE cross-validation errors only show in SOCIE section
          if (!isSOCIESection) {
            return false; // Don't show SOCIE errors in non-SOCIE sections
          }
          // Check if this SOCIE error matches the section's patterns
          return fieldPatterns.some(pattern => 
            error.source.includes(pattern) || error.target.includes(pattern)
          );
        }
        
        // SOCIE sections should ONLY show SOCIE cross-validation errors
        // Don't show generic balance errors (e.g., Assets vs EquityAndLiabilities) in SOCIE
        if (isSOCIESection) {
          return false; // Non-SOCIE errors don't belong in SOCIE sections
        }
        
        // For non-SOCIE sections, use standard pattern matching
        return fieldPatterns.some(pattern => 
          error.source.includes(pattern) || error.target.includes(pattern)
        );
      }
      
      // Check mandatory field errors
      if (error.type === 'mandatory' && error.fieldName && mandatoryFieldsForSection.length > 0) {
        return mandatoryFieldsForSection.includes(error.fieldName);
      }
      
      // Check conditional field errors (conditional fields should also be in mandatory fields list for the section)
      if (error.type === 'conditional' && error.fieldName && mandatoryFieldsForSection.length > 0) {
        return mandatoryFieldsForSection.includes(error.fieldName);
      }
      
      // Check format errors (format errors should also be in mandatory fields list for the section)
      if (error.type === 'format' && error.fieldName && mandatoryFieldsForSection.length > 0) {
        return mandatoryFieldsForSection.includes(error.fieldName);
      }
      
      return false;
    });
  }, []);

  // Get error count for a specific section
  const getSectionErrorCount = useCallback((sectionCode: string): number => {
    return getSectionErrors(sectionCode).length;
  }, [getSectionErrors]);

  // Dummy updateFieldValue for compatibility (ref is updated by EditFilings directly)
  const updateFieldValue = useCallback((_fieldName: string, _contextRef: string, _value: number) => {
    // This is a no-op - ref is updated by EditFilings.handleFieldChange
    // Validation reads directly from ref when needed
  }, []);

  // Check if a specific field has an error (read from ref, not state - NO re-render)
  const hasFieldError = useCallback((fieldName: string, contextRef?: string): boolean => {
    return errorsRef.current.some(error => {
      // IMPORTANT:
      // - Balance errors should highlight BOTH source and target fields.
      // - Conditional errors should highlight ONLY the missing required field (fieldName/source),
      //   NOT the "trigger" field (e.g. NumberOfDirectors... dropdown).
      const matchesField =
        error.fieldName === fieldName ||
        error.source === fieldName ||
        (error.type === 'balance' && error.target === fieldName);
      
      if (contextRef) {
        return matchesField && error.contextRef === contextRef;
      }
      
      return matchesField;
    });
  }, []);

  // Get the error message for a specific field (read from ref, not state - NO re-render)
  const getFieldError = useCallback((fieldName: string, contextRef?: string): ValidationError | null => {
    const error = errorsRef.current.find(error => {
      const matchesField =
        error.fieldName === fieldName ||
        error.source === fieldName ||
        (error.type === 'balance' && error.target === fieldName);
      
      if (contextRef) {
        return matchesField && error.contextRef === contextRef;
      }
      
      return matchesField;
    });
    
    return error || null;
  }, []);

  return {
    errors: errorsRef.current, // Return ref value (for compatibility, but components should NOT read this to avoid re-renders)
    hasErrors: errorsRef.current.length > 0,
    checkValidation,
    clearErrors,
    updateFieldValue, // Kept for interface compatibility
    getSectionErrors,
    getSectionErrorCount,
    hasFieldError,
    getFieldError
  };
};